import React, { ReactNode } from 'react';
import styled from 'styled-components';
import {
  AttributeType,
  Attribute,
  includesAttributesWithTypes
} from '_utils/helpers/attributeType';
import AttributeIcon from './AttributeIcon';
import { dateConverter, getTimeEstimate, getCostEstimate } from '_utils/helpers';
import { AttributeRendererOptions } from './definitions';

const defaultOptions = {
  showIcon: true,
  translations: null
};

const Span = styled.span``;

export function getAttributeRenderer(
  attribute: Attribute,
  options?: AttributeRendererOptions
): () => ReactNode {
  options = { ...defaultOptions, ...options };

  const value = attribute?.value;

  if (value === '' || value === undefined || value === null) {
    return () => null;
  }

  switch (attribute?.type) {
    case AttributeType.location: {
      const LocationRenderer =  () => (
        <Span>
          {options.showIcon && <AttributeIcon attribute={attribute} />}
          {value}
        </Span>
      );
      LocationRenderer.displayName = 'LocationRenderer';
      return LocationRenderer
    }

    case AttributeType.capacity: {
      const noun =
        parseInt(value) === 1
          ? options.translations.capacitySingular
          : options.translations.capacityPlural;
      return () => `${value} ${noun}`;
    }

    case AttributeType.lastUpdated: {
      return () => {
        let date = dateConverter(value)?.visualDate;
        return date
          ? `${
              options?.translations?.publicationLastUpdated
                ? options?.translations?.publicationLastUpdated + ' '
                : ''
            }${date}`
          : null;
      };
    }

    case AttributeType.newsDate: {
      return () => value;
    }

    case AttributeType.whatsOnCost: {
      const WhatsOnCostRenderer = () => (
        <Span>
          {options.showIcon && <AttributeIcon attribute={{ type: 'cost' }} />}
          {value}
        </Span>
      );
      WhatsOnCostRenderer.displayName = 'WhatsOnCostRenderer';
      return WhatsOnCostRenderer;
    }
  }

  return () => null;
}

export function getMultipleAttributeRenderer(
  attributes: { [key: string]: Attribute },
  options?: AttributeRendererOptions
): () => ReactNode {
  options = { ...defaultOptions, ...options };

  if (!attributes) {
    return () => null;
  }

  if (
    includesAttributesWithTypes(attributes, [AttributeType.statusName, AttributeType.statusIcon])
  ) {
    const statusName = attributes[AttributeType.statusName];
    const statusIcon = attributes[AttributeType.statusIcon];

    if (statusName && statusName.value) {
      const StatusRenderer = () => (
        <Span>
          {!!(statusIcon && options.showIcon) && <AttributeIcon attribute={statusIcon} />}
          {statusName.value}
        </Span>
      );
      StatusRenderer.displayName = 'StatusRenderer';
      return StatusRenderer;
    }
  }

  if (
    includesAttributesWithTypes(attributes, [
      AttributeType.timeSpecificity,
      AttributeType.timeUnit,
      AttributeType.timeEstimate
    ])
  ) {
    const timeSpecificity = attributes[AttributeType.timeSpecificity]?.value || '';
    const timeUnit = attributes[AttributeType.timeUnit]?.value || '';
    const timeEstimate = attributes[AttributeType.timeEstimate]?.value || '';

    const time = getTimeEstimate(timeSpecificity, timeUnit, timeEstimate);

    if (time) {
      const TimeRenderer = () => (
        <Span>
          <AttributeIcon attribute={{ type: 'time' }} />
          {time}
        </Span>
      );
      TimeRenderer.displayName = 'TimeRenderer';
      return TimeRenderer;
    } else {
      return null;
    }
  }

  if (
    includesAttributesWithTypes(attributes, [
      AttributeType.costSpecificity,
      AttributeType.costEstimate
    ])
  ) {
    const costEstimate = attributes[AttributeType.costEstimate]?.value || '';
    const costSpecificity = attributes[AttributeType.costSpecificity]?.value || '';

    const cost = getCostEstimate(costSpecificity, costEstimate);

    if (cost) {
      const CostEstimateRender = () => (
        <Span>
          {options.showIcon && <AttributeIcon attribute={{ type: 'cost' }} />}
          {cost}
        </Span>
      );
      CostEstimateRender.displayName = 'CostEstimateRender';
      return CostEstimateRender;
    }
  }

  return () => null;
}
